import type { CrewProjection } from "../domain";

type SinceLastSeen = CrewProjection["since"];

interface CrewSinceLastSeenSummaryProps {
  since: SinceLastSeen;
  fromLabel: string;
}

interface AttentionSinceLastSeenSummaryProps {
  crews: CrewProjection[];
  atRisk: number;
}

function riskCopy(atRisk: number) {
  return `${atRisk} Crew${atRisk === 1 ? "" : "s"} at risk`;
}

export function sinceLastSeenEventCopy(
  since: Pick<SinceLastSeen, "countsAvailable" | "eventCount">,
) {
  return since.countsAvailable ? `${since.eventCount} events` : "change counts unavailable";
}

export function CrewSinceLastSeenSummary({ since, fromLabel }: CrewSinceLastSeenSummaryProps) {
  return (
    <>
      <span className="eyebrow" data-counts-available={since.countsAvailable}>
        Since {fromLabel} · {sinceLastSeenEventCopy(since)}
      </span>
      {since.countsAvailable
        ? <p><strong>{since.completedCount} task{since.completedCount === 1 ? "" : "s"} finished;</strong> {since.summary}</p>
        : <p><strong>{since.summary || "Change counts are unavailable from this Crew checkpoint."}</strong></p>}
    </>
  );
}

export function AttentionSinceLastSeenSummary({ crews, atRisk }: AttentionSinceLastSeenSummaryProps) {
  const hasCrewCheckpoints = crews.length > 0;
  const countsAvailable = hasCrewCheckpoints && crews.every((crew) => crew.since.countsAvailable);
  const completed = crews.reduce((total, crew) => total + crew.since.completedCount, 0);
  const decisions = crews.reduce((total, crew) => total + crew.since.decisionCount, 0);

  return (
    <div className="since-summary" data-counts-available={countsAvailable}>
      <span>Across Crew checkpoints</span>
      <strong>
        {!hasCrewCheckpoints
          ? "No Crew checkpoints available"
          : countsAvailable
          ? `${completed} items finished · ${decisions} decision${decisions === 1 ? "" : "s"} · ${riskCopy(atRisk)}`
          : `Finished-item and decision counts unavailable · ${riskCopy(atRisk)}`}
      </strong>
    </div>
  );
}
